
import {
  EFreelancerRequiredVerfications,
  EAuthMethod,
  EOneTimeLinkSentStatus,
  ELoginStatus
} from '@/helpers/enums'
import { makeLoginErrorState } from '@/helpers/login'

export default {
  name: 'Login',
  beforeRouteEnter(to, from, next) {
    next(async (vm) => {
      try {
        const savedLoginMethod = await vm.getLoginMethodFromLocalStorage()

        if (!savedLoginMethod) {
          vm.toggleAllLoginMethods(true)
        } else {
          vm.savedLoginMethod = savedLoginMethod
          vm.toggleAllLoginMethods(false)
        }

        if (vm.isPhoneVerification) {
          vm.toggleAllLoginMethods(false)
          vm.selectedLoginMethod = EAuthMethod.MOBILE
        }
      } catch (error) {
        console.log('err', error)
      }
      // DON'T REMOVE OTHERWISE IT WILL LOAD FOREVER
      setTimeout(() => vm.$store.commit('PAGE_LOADER', false), 300)
    })
  },

  layout: 'frameless',
  middleware: ['search-redirect', 'getBrowserUuidCookie'],

  async asyncData({ app, query, redirect, store, $auth }) {
    store.commit('PAGE_LOADER', true)
    if (query?.token) {
      const status = await app.$authorizeUser({
        token: query.token,
        endpoint: query?.lm ? 'authorizeWithOtlToken' : 'authorizeWithUrlToken',
        isOtl: query?.lm
      })

      const { projectId, collectedProjectData } = await app.$getRedirectionCookie()

      if (status.login === ELoginStatus.SUCCESS && !projectId && !collectedProjectData) {
        redirect(app.$link.projectSearch())
      } else if (status.login === ELoginStatus.SUCCESS && projectId) {
        redirect(app.$link.project({ id: projectId }))
      } else if (status.login === ELoginStatus.SUCCESS && collectedProjectData) {
        redirect(app.$link.application(collectedProjectData.id))
      } else if (status.login === ELoginStatus.ERROR) {
        status.errorType
          ? redirect(app.$link.login({ otl_s: status.errorType }))
          : redirect(app.$link.login())
      }
    }

    store.commit('PAGE_LOADER', false)
  },

  data() {
    return {
      password: '',
      isLoading: false,
      mobileUnknown: false,
      loginErrorState: makeLoginErrorState(),
      savedLoginMethod: null,
      showAllAvailableLoginMethods: false,
      selectedLoginMethod: null
    }
  },

  head() {
    return this.$headMetaTags({
      title: this.$t('talent.app.meta.login.title')
    })
  },

  computed: {
    freelancerRequiredVerifications() {
      return this.$useConfig(this.$EClientConfig.FREELANCER_REQUIRED_VERIFICATIONS)
    },

    loginMethods() {
      return this.$useConfig(this.$EClientConfig.FREELANCER_LOGIN)
    },

    loginOptions() {
      let buttons = []

      if (this.isPhoneVerification) {
        buttons.push({
          type: EAuthMethod.MOBILE
        })
        return buttons
      }

      if (this.loginMethods.linkedin) {
        buttons.push({
          type: EAuthMethod.LINKEDIN
        })
      }

      if (this.loginMethods.google) {
        buttons.push({
          type: EAuthMethod.GOOGLE
        })
      }

      if (this.loginMethods.auth0) {
        buttons.push({
          type: EAuthMethod.AUTH0
        })
      }

      if (!this.isPhoneVerification) {
        buttons.push({
          type: EAuthMethod.EMAIL
        })
      }

      if (this.isOneTimeLinkAvailable) {
        const oneTimeLinkBtn = {
          type: EAuthMethod.ONE_TIME_LINK
        }

        buttons.push(oneTimeLinkBtn)
        if (this.shouldSuggestOneTimeLogin && !this.showAllAvailableLoginMethods) {
          buttons = [
            { ...oneTimeLinkBtn, text: this.$t('talent.login.continue.with.one.time.link') }
          ]
        }
      }

      return buttons
    },

    isPhoneVerification() {
      return (
        this.freelancerRequiredVerifications.mobileNumber ===
        EFreelancerRequiredVerfications.PRINCIPAL
      )
    },
    isOneTimeLinkAvailable() {
      return (
        this.freelancerRequiredVerifications.email === EFreelancerRequiredVerfications.PRINCIPAL &&
        this.freelancerRequiredVerifications.mobileNumber ===
          EFreelancerRequiredVerfications.DISABLED
      )
    },

    isEmailLoginSelected() {
      return this.selectedLoginMethod === EAuthMethod.EMAIL
    },
    isOneTimeLinkSelected() {
      return this.selectedLoginMethod === EAuthMethod.ONE_TIME_LINK
    },

    oneTimeLinkType() {
      return EAuthMethod.ONE_TIME_LINK
    },

    shouldSuggestOneTimeLogin() {
      return (
        this.loginErrorState?.errorType?.includes(EAuthMethod.EMAIL) &&
        this.isOneTimeLinkAvailable &&
        this.isEmailLoginSelected &&
        !this.loginErrorState?.blockedFor
      )
    },
    shouldShowNotificationOtl() {
      return (
        this.$route?.query?.otl_s &&
        this.$route?.query?.otl_s !== EOneTimeLinkSentStatus.NOT_TRIGERRED
      )
    },
    oneTimeLinkStatus() {
      return this.$route?.query?.otl_s || EOneTimeLinkSentStatus.NOT_TRIGERRED
    }
  },

  methods: {
    async submitForm({ email, password }) {
      this.$store.commit('PAGE_LOADER', true)

      try {
        this.$store.commit('PAGE_LOADER', true)

        await this.$auth.loginWith('local', {
          data: {
            email,
            password
          }
        })

        await this.$pushEvent('loginSuccessful')
        await this.$getBrowserUUID()
        this.loginErrorState = makeLoginErrorState()

        const { projectId, collectedProjectData } = await this.$getRedirectionCookie()

        if (collectedProjectData?.id) {
          this.$router.push(this.$link.application(collectedProjectData.id))
        } else if (projectId) {
          this.$router.push(this.$link.project({ id: projectId }))
        }

        this.$toast.fire(this.$t('talent.login.welcome.back.login.success'), { type: 'success' })
      } catch (error) {
        this.$store.commit('PAGE_LOADER', false)
        await this.$pushEvent('loginFailed')

        const errorInfos = {
          ...error,
          errorType: error?.response?.data?.blockedFor
            ? [EAuthMethod.EMAIL, EAuthMethod.ONE_TIME_LINK]
            : [EAuthMethod.EMAIL]
        }

        // Add specific verbiage for login 401 error message
        if (
          errorInfos.errorType.includes(EAuthMethod.EMAIL) &&
          errorInfos.response?.status === 401 &&
          errorInfos.response?.data?.messages[0] === 'api.error.authorize'
        ) {
          errorInfos.response.data.messages.unshift('api.error.authorize.email.account.warning')
        }

        this.loginErrorState = makeLoginErrorState(errorInfos)
      }
    },
    async submitOneTimeLinkForm({ email }) {
      try {
        this.$store.commit('PAGE_LOADER', true)

        const payload = {
          email,
          path: `/app/login?lm=${EAuthMethod.ONE_TIME_LINK}`,
          deviceUuid: this.$trustedToken()
        }

        await this.$api.login.getOneTimeLinkLogin(payload)

        await this.$pushEvent('oneTimeLinkSent')

        this.$router.push(this.$link.login({ otl_s: EOneTimeLinkSentStatus.SENT }))
      } catch (error) {
        console.log('ERROR', { ...error })
        const err = {
          ...error,
          errorType: [EAuthMethod.ONE_TIME_LINK]
        }

        this.loginErrorState = makeLoginErrorState(err)

        await this.$pushEvent('oneTimeLinkSendingFailed')
      } finally {
        this.$store.commit('PAGE_LOADER', false)
      }
    },

    async submitFormMobile({ mobileCode, mobileNumber }) {
      this.isLoading = true
      try {
        const payload = {
          mobileCode,
          mobileNumber
        }

        await this.$api.freelancer.resetPhoneCode(payload)

        this.$store.commit('mobile/IS_REGISTERED', true)
        this.$router.push(`mobile-activation/${mobileCode}${mobileNumber}`)
        this.isLoading = false
      } catch (err) {
        this.isLoading = false
        this.mobileUnknown = true
        this.$toast.fire(this.$t('talent.register.mobile.not.send.code'), {
          timeout: 5000,
          type: 'error'
        })
      }
    },

    deactivated() {
      return this.$route.query?.deactivated
    },

    handleSelectedMethod(method) {
      const formMethods = [EAuthMethod.EMAIL, EAuthMethod.ONE_TIME_LINK, EAuthMethod.MOBILE]

      if (formMethods.includes(method)) {
        this.selectedLoginMethod = method
        this.toggleAllLoginMethods(false)
      }

      this.saveLoginMethodInLocalStorage(method)
      if (this.$route.query?.lm) return this.$router.push(this.$link.login())
    },

    saveLoginMethodInLocalStorage(method) {
      localStorage.setItem('loginMethod', method)
    },

    getLoginMethodFromLocalStorage() {
      return localStorage.getItem('loginMethod')
    },

    toggleAllLoginMethods(value) {
      this.showAllAvailableLoginMethods = value
    },
    async authorizeUser({ token, endpoint, isOtl }) {
      try {
        const { data } = await this.$api.login[endpoint]({
          urlToken: token,
          actorDeviceUUID: this.$trustedToken()
        })

        await this.$auth.setUserToken(data.access_token, data.refresh_token)
        return { login: 'success' }
      } catch ({ response }) {
        let errorType = null
        if (isOtl) {
          errorType =
            response?.status === 400
              ? EOneTimeLinkSentStatus.EXPIRED
              : EOneTimeLinkSentStatus.INVALID
        }

        return { login: 'error', errorType }
      }
    }
  }
}
